/**
 * /profile — self-service profile settings. The signed-in user views their own
 * B2BUser doc (GET /users/logged_in_user). First name / last name / phone sit
 * in the Account card with a pencil each that opens ProfileFieldEditDialog and
 * saves via POST /users/update_user_by_id/:id. The other account fields
 * (email/roles/permissions/company/teams) are read-only here — roles aren't
 * self-editable (privilege escalation). Password changes reuse the existing
 * ChangePasswordDialog.
 */
import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import EditOutlined from "@mui/icons-material/EditOutlined";
import LockResetOutlined from "@mui/icons-material/LockResetOutlined";
import { useSnackbar } from "notistack";

import { useUsersApi } from "../../hooks/useAdminUsersHook";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { formatPhone } from "../../_src_shared/utils/functions/formatPhone";
import { T } from "../SupportScreen/components/supportUi";
import ChangePasswordDialog from "../../AuthenticatedDashboard/ChangePasswordDialog";
import ProfileCachedDataCard from "./ProfileCachedDataCard";
import ProfileAuthTokenCard from "./ProfileAuthTokenCard";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import ProfileFieldEditDialog, { PROFILE_EDIT_FIELDS } from "./ProfileFieldEditDialog";

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const userId = (u) => String(u?._id ?? u?.id ?? "");
const cleanPhone = (p) => (p && p !== "None" ? p : "");

const Card = ({ title, subtitle, children, action }) => (
  <Box
    sx={{
      background: T.card,
      border: `1px solid ${T.line}`,
      borderRadius: T.radius,
      boxShadow: T.shadow,
      p: "20px 22px",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
      <Box>
        <Box sx={{ fontSize: 15, fontWeight: 700, color: T.ink900 }}>{title}</Box>
        {subtitle && (
          <Box sx={{ fontSize: 12.5, color: T.ink500, mt: "3px" }}>{subtitle}</Box>
        )}
      </Box>
      {action && <Box sx={{ ml: "auto" }}>{action}</Box>}
    </Box>
    {children}
  </Box>
);

const Tag = ({ label, tone = "slate" }) => {
  const tones = {
    slate: { bg: "#f1f4f6", fg: T.ink700 },
    teal: { bg: T.teal50, fg: T.teal600 },
    purple: { bg: "#f3eefe", fg: "#7c3aed" },
  };
  const m = tones[tone] || tones.slate;
  return (
    <Box
      component="span"
      sx={{
        fontSize: "11.5px",
        fontWeight: 600,
        color: m.fg,
        background: m.bg,
        borderRadius: "6px",
        px: "8px",
        py: "3px",
      }}
    >
      {label}
    </Box>
  );
};

const ReadRow = ({ label, children, onEdit }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      py: 1,
      borderBottom: `1px solid ${T.line}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Box sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink500, width: 130, flexShrink: 0 }}>
      {label}
    </Box>
    <Box sx={{ fontSize: 13.5, color: T.ink900, display: "flex", flexWrap: "wrap", gap: "5px", minWidth: 0 }}>
      {children}
    </Box>
    {onEdit && (
      <Tooltip title={`Edit ${label.toLowerCase()}`}>
        <IconButton
          size="small"
          onClick={onEdit}
          aria-label={`Edit ${label.toLowerCase()}`}
          sx={{ ml: "auto", color: T.ink500, "&:hover": { color: T.teal600 } }}
        >
          <EditOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

export default function ProfileScreen() {
  useDocumentTitle("Profile");
  const { getMyProfile, updateUser } = useUsersApi();
  const { enqueueSnackbar } = useSnackbar();
  const { isSuperAdmin } = usePermissionsHook();

  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  // Which Account-card field the edit modal is open for (null = closed).
  const [editField, setEditField] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    getMyProfile()
      .then((u) => alive && setMe(u))
      .catch((e) => alive && setLoadError(e?.response?.data?.detail ?? e?.message ?? "Failed to load profile"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [getMyProfile]);

  const openEdit = (key) => {
    setSaveError(null);
    setEditField(PROFILE_EDIT_FIELDS[key]);
  };
  const closeEdit = () => {
    setEditField(null);
    setSaveError(null);
  };

  // Posts only the edited key — update_user_by_id $sets whatever is sent.
  async function save(key, value) {
    setSaveError(null);
    setSubmitting(true);
    try {
      const patch = { [key]: value ?? "" };
      const updated = await updateUser(userId(me), patch);
      setMe((prev) => ({ ...prev, ...updated, ...patch }));
      enqueueSnackbar("Profile updated", { variant: "success" });
      setEditField(null);
    } catch (err) {
      setSaveError(err?.response?.data?.detail ?? err?.message ?? "Update failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ background: T.bg, minHeight: "100%", height: "100%", overflowY: "auto" }}>
      <Container maxWidth="md" sx={{ pt: "32px", pb: "64px" }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ fontSize: 23, fontWeight: 700, letterSpacing: "-.01em", color: T.ink900 }}>
            Profile Settings
          </Box>
          <Box sx={{ color: T.ink500, mt: "5px", fontSize: 13.5 }}>
            Manage your account details.
          </Box>
        </Box>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress size={26} />
          </Box>
        ) : me ? (
          <Stack spacing={2.5}>
            <Card
              title="Account"
              subtitle="Name and phone are visible to your team across the portal. Everything else is managed by an administrator."
            >
              <ReadRow label="First name" onEdit={() => openEdit("firstName")}>
                {me.firstName || "—"}
              </ReadRow>
              <ReadRow label="Last name" onEdit={() => openEdit("lastName")}>
                {me.lastName || "—"}
              </ReadRow>
              <ReadRow label="Phone" onEdit={() => openEdit("phone_number")}>
                {cleanPhone(me.phone_number) ? formatPhone(cleanPhone(me.phone_number)) : "—"}
              </ReadRow>
              <ReadRow label="Email">{me.emailAddress || "—"}</ReadRow>
              <ReadRow label="Company">{me.company_name || "—"}</ReadRow>
              <ReadRow label="Roles">
                {asArray(me.roles).length
                  ? asArray(me.roles).map((r) => (
                      <Tag key={r} label={r} tone={r === "admin" ? "teal" : "slate"} />
                    ))
                  : "—"}
              </ReadRow>
              <ReadRow label="Permissions">
                {asArray(me.permissions).length
                  ? asArray(me.permissions).map((p) => <Tag key={p} label={p} />)
                  : "—"}
              </ReadRow>
              <ReadRow label="Teams">
                {asArray(me.teams).length
                  ? asArray(me.teams).map((t) => <Tag key={t} label={t} tone="purple" />)
                  : "—"}
              </ReadRow>
              <ReadRow label="Member since">
                {me.createdDate ? formatDateTime(me.createdDate, "date") : "—"}
              </ReadRow>
            </Card>

            <Card
              title="Security"
              subtitle="Set a new password for your account."
              action={
                <Button
                  variant="outlined"
                  startIcon={<LockResetOutlined />}
                  onClick={() => setPwOpen(true)}
                  sx={{
                    color: T.ink700,
                    borderColor: T.line,
                    fontWeight: 600,
                    borderRadius: "8px",
                    "&:hover": { borderColor: T.ink300, background: "#f8fafb" },
                  }}
                >
                  Change Password
                </Button>
              }
            >
              <Box sx={{ fontSize: 13, color: T.ink500 }}>
                Changing your password signs you out of other sessions.
              </Box>
            </Card>

            {/* Super admins only: the sign-in token, for scripts that act as them. */}
            {isSuperAdmin ? <ProfileAuthTokenCard Card={Card} /> : null}

            <ProfileCachedDataCard />
          </Stack>
        ) : null}
      </Container>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
      <ProfileFieldEditDialog
        open={Boolean(editField)}
        field={editField}
        value={
          editField?.key === "phone_number"
            ? cleanPhone(me?.phone_number)
            : me?.[editField?.key] || ""
        }
        submitting={submitting}
        error={saveError}
        onSave={save}
        onClose={closeEdit}
      />
    </Box>
  );
}
